import clsx from 'clsx';
import {useCallback, useMemo} from 'react';
import {IconCloseV2} from '~/components';
import {DATA_TEST_IDS} from '~/lib/const';
import {FilterStateType} from '~/types';

function FilterTag({
  className,
  values,
  name,
  filterOptionId,
  onClickRemove,
}: {
  className?: string;
  values: string[];
  name: string;
  filterOptionId: string;
  onClickRemove: (key: string, value: string) => void;
}) {
  const handleClickClear = useCallback(
    (value: string) => {
      onClickRemove(filterOptionId, value);
    },
    [filterOptionId, onClickRemove],
  );

  return (
    <div
      className={clsx(
        'flex justify-center items-center px-5 py-3 md:py-[15px] bg-gray-200 rounded-primary',
        className,
      )}
    >
      <span
        className={
          'font-header font-bold tracking-wider text-center text-14/16 md:text-16/20 mr-2'
        }
      >
        {name}:
      </span>
      <div className="flex items-center gap-2">
        {values?.map((value) => (
          <div
            key={value}
            className="flex items-center gap-1 pl-3 pr-1 py-1 bg-gray-250 rounded-primary"
          >
            <span
              data-testid={DATA_TEST_IDS.FILTER_TAG(value)}
              className={
                'font-header font-bold tracking-wider text-center text-14/16 md:text-16/20 uppercase'
              }
            >
              {value}
            </span>

            <button
              data-testid={DATA_TEST_IDS.BUTTON('clear')}
              type={'button'}
              className={'px-2'}
              aria-label={`remove filter ${value}`}
              onClick={() => handleClickClear(value)}
            >
              <IconCloseV2 className={'w-3 h3'} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export type FilterOptionsByIdType = {[key: string]: {name: string}};

export function ProductFilterTags({
  filters: filtersApplied,
  filterOptionsById,
  onClickClearFilters,
  onClickRemoveFilter,
}: {
  filters: FilterStateType;
  filterOptionsById: FilterOptionsByIdType;
  onClickClearFilters: () => void;
  onClickRemoveFilter: (key: string, value: string) => void;
}) {
  const filters = useMemo(() => {
    return Object.keys(filtersApplied)
      .map((key) => {
        const value = filtersApplied[key];
        const name = filterOptionsById[key].name ?? '';

        if (typeof value === 'object') {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              return {
                filterOptionId: key,
                name,
                value: value.map((v) => {
                  const parts = (v ?? '').toString().split(':');

                  return parts.length > 0 ? parts.at(-1) : v;
                }),
              };
            }

            return undefined;
          } else {
            return {
              filterOptionId: key,
              name,
              value: `${value.min} - ${value.max}`,
            };
          }
        } else if (value !== undefined) {
          const parts = (value ?? '').split(':');

          return {
            filterOptionId: key,
            name,
            value: value
              ? parts.length > 0
                ? parts.at(-1)
                : value
              : undefined,
          };
        }
      })
      .filter(Boolean);
  }, [filtersApplied]);

  if (filters.length === 0) return null;

  return (
    <div
      data-testid={DATA_TEST_IDS.PRODUCT_FILTERS_TAG}
      className={
        'flex flex-row flex-wrap justify-start items-center -ml-2.5 md:-ml-[21px] -mt-2.5'
      }
    >
      {filters.map((filter) => {
        if (!filter) {
          return null;
        }

        const values = filter.value
          ? Array.isArray(filter.value)
            ? filter.value
            : [filter.value]
          : [];

        if (values.length === 0) return null;

        return (
          <FilterTag
            key={String(filter.filterOptionId)}
            className={'ml-2.5 md:ml-[21px] mt-2.5'}
            values={values.map((val) => String(val))}
            name={filter.name}
            filterOptionId={filter.filterOptionId}
            onClickRemove={onClickRemoveFilter}
          />
        );
      })}
      <button
        data-testid={DATA_TEST_IDS.BUTTON('clear-all')}
        type={'button'}
        className={
          'ml-2.5 md:ml-5 mt-2.5 mx-2.5 font-header tracking-wider font-bold text-14/16 md:text-16/20 border-b-2 border-solid border-black pb-[1px]'
        }
        onClick={onClickClearFilters}
      >
        CLEAR ALL
      </button>
    </div>
  );
}
